/**
 * 사업등록 PoC 저장소 (tenant별 localStorage)
 * - 사업리스트·속성정보는 동일 행(단일 테이블). UI만 분리.
 * - 분야·부문은 사업등록에서 관리 (구조화 화면에는 없음)
 */
import {
  YN_OPTIONS,
  BUDGET_TYPE_DEFAULT,
  STRUCTURE_BUDGET_TYPE,
  BUDGET_TYPE_CAPITAL,
  isBudgetTypeAll,
  buildSubjectTree,
} from 'budget/budgetStructureStore';
import { getConfirmedProjectStructure } from 'budget/budgetProjectStructureStore';
import { createProjectUid, ensureProjectsIdentity } from 'budget/budgetProjectHistoryStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

export { YN_OPTIONS, buildSubjectTree };

export const PROJECT_STORAGE_PREFIX = 'budgetProject';

const PROJECT_SAVE_QUERY = [
  'BdProject.insertProject',
  'BdProject.updateProject',
  'BdProject.deleteProject',
];

const projectCache = new Map();

export const PROJECT_PURPOSE = '집행';

export const GNK_TENANT_CODE = 'GNK';

/** GNK 테넌트 여부 */
export function isGnkTenant(tenantCode) {
  return String(tenantCode || '').trim().toUpperCase() === GNK_TENANT_CODE;
}

/** GNK 사업등록 초기화(1회) — localStorage 플래그 */
export const GNK_PROJECT_RESET_FLAG = 'erp.gnk.project.reset.v1';

/** 기관별 사업등록·사업이력 localStorage 전체 삭제 */
export function clearProjectStorageForTenant(tenantCode) {
  const tenant = String(tenantCode || '').trim();
  if (!tenant || typeof localStorage === 'undefined') return 0;
  let removed = 0;
  Object.keys(localStorage).forEach((key) => {
    if (
      key.startsWith(`${PROJECT_STORAGE_PREFIX}|${tenant}|`)
      || key.startsWith(`budgetProjectHistory|${tenant}|`)
    ) {
      localStorage.removeItem(key);
      removed += 1;
    }
  });
  projectCache.clear();
  return removed;
}

/** 사업등록 키: 기관·회계연도·목적 (수지구분·예산구분은 행 단위) */
export function projectStorageKey({ tenantCode, fiscalYear, purpose }) {
  return [
    PROJECT_STORAGE_PREFIX,
    tenantCode || '',
    fiscalYear || '',
    purpose || PROJECT_PURPOSE,
  ].join('|');
}

function legacyProjectStorageKeys({ tenantCode, fiscalYear, purpose, inoutDiv, budgetType }) {
  const purposeVal = purpose || PROJECT_PURPOSE;
  const inouts = inoutDiv ? [inoutDiv] : ['지출', '수입'];
  const types =
    budgetType && !isBudgetTypeAll(budgetType)
      ? [budgetType]
      : [STRUCTURE_BUDGET_TYPE, BUDGET_TYPE_CAPITAL];
  const keys = [];
  inouts.forEach((io) => {
    types.forEach((bt) => {
      keys.push(
        [PROJECT_STORAGE_PREFIX, tenantCode || '', fiscalYear || '', io, purposeVal, bt].join('|')
      );
    });
  });
  return keys;
}

/** attr_json ↔ 속성정보 flat 필드 (사업리스트·속성정보는 동일 행) */
function mapApiRowToProject(row) {
  let attrs = {};
  try {
    if (row?.attrJson) attrs = JSON.parse(row.attrJson) || {};
  } catch {
    attrs = {};
  }
  return { ...PROJECT_ATTR_DEFAULTS, ...attrs, ...row, mod: '' };
}

function projectPersistPayload(row, dims) {
  const {
    mod,
    _tempId,
    id,
    tenantId,
    tenant_id: _tid,
    tenantCode: _tc,
    tenant_code: _tcode,
    version,
    attrJson: _drop,
    projectUid,
    projectCode,
    projectName,
    parentCode,
    levelName,
    pathCode,
    finalYn,
    startYear,
    endYear,
    sortOrder,
    useYn,
    remark,
    budgetType,
    fiscalYear: _fy,
    ...attrs
  } = row || {};
  return {
    projectUid: projectUid || createProjectUid(),
    fiscalYear: dims?.fiscalYear,
    budgetType: budgetType || STRUCTURE_BUDGET_TYPE,
    projectCode,
    projectName,
    parentCode: parentCode || null,
    levelName: levelName || null,
    pathCode: pathCode || null,
    finalYn: finalYn || 'N',
    startYear: startYear || null,
    endYear: endYear || null,
    sortOrder,
    useYn: useYn || 'Y',
    remark: remark || null,
    attrJson: Object.keys(attrs).length ? JSON.stringify(attrs) : null,
    version,
    mod,
  };
}

/** 동기 조회용 — load 가 채운 캐시(API) 또는 localStorage(로컬) */
export function getCachedProjects(dims) {
  const key = projectStorageKey(dims);
  const cached = cacheGet(projectCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readProjectsFromStorage(dims);
  cacheSet(projectCache, key, rows);
  return rows;
}

function readProjectsFromStorage(dims) {
  try {
    const primary = projectStorageKey(dims);
    let raw = localStorage.getItem(primary);
    if (!raw) {
      const merged = [];
      const seen = new Set();
      for (const key of legacyProjectStorageKeys(dims || {})) {
        const legacyRaw = localStorage.getItem(key);
        if (!legacyRaw) continue;
        try {
          const data = JSON.parse(legacyRaw);
          const rows = Array.isArray(data?.rows) ? data.rows : [];
          rows.forEach((r) => {
            const k = `${r?.budgetType || ''}::${r?.projectCode || ''}`;
            if (!seen.has(k)) {
              seen.add(k);
              merged.push(r);
            }
          });
        } catch {
          /* skip */
        }
      }
      return ensureProjectsIdentity(merged);
    }
    const data = JSON.parse(raw);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return ensureProjectsIdentity(rows);
  } catch {
    return [];
  }
}

export async function loadProjects(dims) {
  const key = projectStorageKey(dims);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdProject.selectProjectList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
    });
    const mapped = ensureProjectsIdentity(rows.map((r) => mapApiRowToProject(r)));
    cacheSet(projectCache, key, mapped);
    return mapped;
  }
  const rows = readProjectsFromStorage(dims);
  cacheSet(projectCache, key, rows);
  return rows;
}

export async function saveProjects(dims, rows) {
  const key = projectStorageKey(dims);
  const normalized = ensureProjectsIdentity(rows || []);

  if (isBdApiEnabled()) {
    const hasMod = normalized.some((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'));
    let payload;
    if (hasMod) {
      payload = normalized
        .filter((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'))
        .map((r) => projectPersistPayload(r, dims));
    } else {
      // 이관·시드처럼 mod 없이 전량 전달되는 경우 — PK 존재 여부로 insert/update 판단
      const existing = await commonSelect('BdProject.selectProjectList', {
        tenantCode: dims?.tenantCode,
        fiscalYear: dims?.fiscalYear,
      });
      const dbUids = new Set(existing.map((r) => r.projectUid));
      payload = normalized.map((r) => ({
        ...projectPersistPayload(r, dims),
        mod: dbUids.has(r.projectUid) ? 'U' : 'A',
      }));
    }
    await commonSaveList(PROJECT_SAVE_QUERY, payload, {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
    });
    return loadProjects(dims);
  }

  localStorage.setItem(
    key,
    JSON.stringify({
      dims: { ...dims, purpose: dims.purpose || PROJECT_PURPOSE },
      rows: normalized,
      savedAt: new Date().toISOString(),
    })
  );
  cacheSet(projectCache, key, normalized);
  return normalized;
}

/** 속성정보 패널 필드 (마스터와 동일 행) */
export const PROJECT_ATTR_DEFAULTS = {
  fieldCode: null,
  fieldName: '',
  sectorCode: null,
  sectorName: '',
  fieldSectorNm: '',
  budgetDeptCode: null,
  budgetDeptNm: '',
  inputYn: 'Y',
};

export function defaultProjectRow(partial = {}) {
  return {
    levelName: '',
    budgetType: STRUCTURE_BUDGET_TYPE,
    parentCode: '',
    projectCode: '',
    projectName: '',
    projectUid: createProjectUid(),
    pathCode: '',
    useYn: 'Y',
    startYear: '',
    endYear: '',
    sortOrder: 10,
    remark: '',
    ...PROJECT_ATTR_DEFAULTS,
    ...partial,
  };
}

/**
 * 확정 구조 + 샘플 사업 시드 (물 테마 PoC)
 * applyLevel(최심층) 행에 최종=Y
 */
export function seedSampleProjects(structure, budgetType) {
  if (!structure?.usedLevels?.length) return [];
  const levels = structure.usedLevels;
  const bt = budgetType || STRUCTURE_BUDGET_TYPE;
  const applyName = structure.applyLevelName || levels[levels.length - 1]?.levelName;
  const rows = [];

  const policy = levels[0];
  const unit = levels[1];
  const detail = levels.find((l) => l.levelName === applyName) || levels[levels.length - 1];

  const push = (partial) => {
    rows.push(
      defaultProjectRow({
        budgetType: bt,
        finalYn: partial.levelName === applyName ? 'Y' : 'N',
        ...partial,
      })
    );
  };

  if (policy) {
    push({
      levelName: policy.levelName,
      parentCode: '',
      projectCode: '100000',
      projectName: '고품질의 수돗물생산',
      sortOrder: 10,
      startYear: '2005',
      endYear: '2100',
    });
    push({
      levelName: policy.levelName,
      parentCode: '',
      projectCode: '200000',
      projectName: '최적의 수돗물 공급체계 구축',
      sortOrder: 20,
      startYear: '2005',
      endYear: '2100',
    });
  }
  if (unit && policy) {
    push({
      levelName: unit.levelName,
      parentCode: '100000',
      projectCode: '110000',
      projectName: '정수장 운영',
      sortOrder: 30,
      startYear: '2005',
      endYear: '2100',
    });
  }
  if (detail && unit) {
    push({
      levelName: detail.levelName,
      parentCode: unit ? '110000' : '100000',
      projectCode: '000001',
      projectName: '상수관로 불탐구간 현행화 조사 용역',
      sortOrder: 40,
      startYear: '2021',
      endYear: '2022',
      fieldSectorNm: '070-071 상하수도·수질',
      fieldCode: '070',
      fieldName: '환경보호',
      sectorCode: '071',
      sectorName: '상하수도·수질',
      budgetDeptNm: '상수도사업본부',
      inputYn: 'Y',
    });
    push({
      levelName: detail.levelName,
      parentCode: unit ? '110000' : '100000',
      projectCode: '000000',
      projectName: '대표사업',
      sortOrder: 50,
      startYear: '2005',
      endYear: '2100',
      inputYn: 'Y',
    });
  }

  return ensureProjectsIdentity(rows);
}

export async function loadOrSeedProjects(dims) {
  const existing = await loadProjects(dims);
  if (existing.length) return existing;
  // API·GNK: 자동 seed 금지 (사용자가 DB에 INSERT)
  if (isBdApiEnabled() || isGnkTenant(dims?.tenantCode)) return [];
  const structure = getConfirmedProjectStructure(dims);
  if (!structure) return [];
  const seeded = seedSampleProjects(structure, STRUCTURE_BUDGET_TYPE);
  if (seeded.length) await saveProjects(dims, seeded.map((r) => ({ ...r, mod: 'A' })));
  return seeded;
}

export { BUDGET_TYPE_DEFAULT, STRUCTURE_BUDGET_TYPE, isBudgetTypeAll, getConfirmedProjectStructure };
